import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Tab } from '@headlessui/react';

import type { Pagination } from '../../output/models/Pagination';

import { CircularProgress, DateSelect, PageList } from '../../components';

import { DisclosureTable } from './DisclosureTable';
import { FinancialInformationTable } from './FinancialInformationTable';
import { FinancialInformationTableSP } from './FinancialInformationTableSP';

import { useData } from './useData';

import { usePage } from '../../hooks';
import { currentDateByHour, disclosuresPath, jpDate, tabClassName } from '../../utils';

export const DisclosuresPage: React.FC = () => {
  const params = useParams();
  const dateStr = params.date ?? currentDateByHour();
  const page = usePage();
  const { data, pagination, lastUpdated, loading } = useData(dateStr, page);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const navigate = useNavigate();

  const onDateSelect = (date: string) => {
    navigate(disclosuresPath(date));
  };

  useEffect(() => {
    // タブ選択初期化
    setSelectedIndex(0);
  }, [dateStr]);

  return (
    <div>
      <DateSelect selectedDateStr={dateStr} onDateSelect={onDateSelect} />
      {!data ? (
        <div className="relative mt-3.75 w-full text-sm leading-base md:text-xs md:leading-base">
          <CircularProgress variant={'full'} />
          <span>{jpDate(dateStr)}の決算短信を取得しています。</span>
        </div>
      ) : (
        <>
          <div className="relative mt-3.75 text-sm leading-base md:text-xs md:leading-base">
            {loading && <CircularProgress variant={'full'} />}
            <span>{paginationText(jpDate(dateStr), pagination)}</span>
          </div>
          {pagination.count > 0 && (
            <>
              <PageList pagination={pagination} currentPage={Number(page)} className="mt-3.75" />
              <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                <Tab.List className="mt-4.75 border-b border-gray text-sm md:text-xs">
                  <Tab className={({ selected }) => tabClassName(selected)}>
                    決算短信・業績予想の修正
                  </Tab>
                  <Tab className={({ selected }) => tabClassName(selected)}>月次</Tab>
                  <Tab className={({ selected }) => tabClassName(selected)}>全て</Tab>
                  {lastUpdated !== '' && (
                    <span className="float-right mb-1.5 pt-3.75 md:mb-0">
                      株価反映日時：{lastUpdated}
                    </span>
                  )}
                </Tab.List>
                <Tab.Panels className="mt-5">
                  <Tab.Panel unmount={false}>
                    <FinancialInformationTable data={data.financials} />
                    <FinancialInformationTableSP data={data.financials} />
                  </Tab.Panel>
                  <Tab.Panel unmount={false}>
                    <DisclosureTable data={data.monthlies} monthly={true} />
                  </Tab.Panel>
                  <Tab.Panel unmount={false}>
                    <DisclosureTable data={data.disclosures} monthly={false} />
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
              <PageList pagination={pagination} currentPage={Number(page)} className="mt-5" />
            </>
          )}
        </>
      )}
    </div>
  );
};

const paginationText = (date: string, pagination: Pagination): string => {
  if (pagination.count === 0) {
    return `${date}に開示された情報はありません。`;
  } else {
    return `${date}の開示情報を${pagination.count}件中${pagination.from} - ${pagination.to}件表示しています。`;
  }
};
